import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createVNode as _createVNode, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "main-table__wrapper" }
const _hoisted_2 = { class: "main-table" }
const _hoisted_3 = {
  class: "main-table__row",
  style: { gridTemplateColumns: 'minmax(700px, 2fr) repeat(12, minmax(120px, 1fr))' }
}
const _hoisted_4 = ["onClick"]
const _hoisted_5 = { class: "main-table__message" }
const _hoisted_6 = { class: "main-table__col main-table__col--nested main-table__col--sticky" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "main-table__message" }
const _hoisted_9 = { class: "main-table__message" }
const _hoisted_10 = { class: "main-table__col main-table__col--nested main-table__col--nested-2 main-table__col--sticky" }
const _hoisted_11 = { class: "main-table__message" }
const _hoisted_12 = { class: "main-table__message" }

import { ref, onMounted, onBeforeUnmount, computed, watch } from "vue";
import PerfectScrollbar from "perfect-scrollbar";
import "perfect-scrollbar/css/perfect-scrollbar.css";
import IconChevronUp from "@/assets/icons/IconChevronUp.vue";
import { reportYearParamsList } from "@/composables/list/params.list";
import type { Report, MonthlyData, YearData } from "@/types/reports";

// Тип для ключей YearData, которые содержат MonthlyData
type MonthlyDataKeys = {
  [K in keyof YearData]: YearData[K] extends MonthlyData ? K : never;
}[keyof YearData];

interface TableRow {
  parameter: string;
  paramKey: string;
  [key: `month_${number}`]: number | string | null;
}

// Месяца
type MonthColumn = `month_${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12}`;

// Все возможные ключи колонок
type ColumnKey = "parameter" | "paramKey" | MonthColumn;

// Интерфейс колонки
interface Column {
  key: ColumnKey;
  label: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'FileReportTable',
  props: {
    report: {}
  },
  setup(__props: any) {

const props = __props;

// Текущий отчет
const currentMonthReport = computed(() => props.report);

// Названия месяцев
const monthNames = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"
];

// Виртуальные параметры (для группировки, без данных)
const VIRTUAL_PARAMS: Record<string, string> = {
  prosecutor_decisions_group: "Принято решений прокурором (из стр. 5)",
  prosecutor_decisions_court_group: "Направлено в суд",
  suspended_group: "Приостановленно"
};

// Конфигурация вложенных параметров (поддерживает 2 уровня вложенности)
const NESTED_PARAMS: Record<string, string[]> = {
  prosecutor_decisions_group: [
    "prosecutor_decisions_court_group",
    "prosecutor_decisions_terminated",
    "returned_for_investigation"
  ],
  prosecutor_decisions_court_group: [
    "prosecutor_decisions_to_court",
    "prosecutor_decisions_medical_measures"
  ],
  suspended_group: [
    "suspended_unidentified_person",
    "suspended_location_unknown",
    "suspended_no_possibility"
  ]
};

// Порядок отображения параметров
const PARAMS_ORDER = [
  "accepted_to_production",
  "completed_cases",
  "sent_to_prosecutor",
  "prosecutor_decisions_group", // виртуальный параметр
  "court_petition_427",
  "court_petition_446",
  "terminated_cases",
  "suspended_group", // виртуальный параметр
  "investigated_overdue",
  "terminated_rehabilitating",
  "terminated_rehabilitating_detained",
  "acquitted_and_terminated",
  "acquitted_and_terminated_detained"
];

// Все параметры (реальные + виртуальные) - используется для поиска в getNestedParams
const allParameters = [
  ...Object.entries(reportYearParamsList).map(([key, label]) => ({ key, label })),
  ...Object.entries(VIRTUAL_PARAMS).map(([key, label]) => ({ key, label }))
];

// Конфигурация параметров 1 уровня
const parameters = PARAMS_ORDER.map(key => {
  const param = allParameters.find(p => p.key === key);
  return param || null;
}).filter((item): item is { key: string; label: string } => item !== null);

// Преобразование номера месяца (1-12) в ключ MonthlyData
const getMonthKey = (monthNumber: number): keyof MonthlyData => {
  const monthKeys: (keyof MonthlyData)[] = [
    "january", "february", "march", "april", "may", "june",
    "july", "august", "september", "october", "november", "december"
  ];
  return monthKeys[monthNumber - 1];
};

// Проверка, является ли параметр виртуальным
const isVirtualParam = (paramKey: string): boolean => {
  return paramKey in VIRTUAL_PARAMS;
};

// Конфигурация колонок: первая колонка - параметр, остальные - все 12 месяцев
const columns = computed((): Column[] => {
  const cols: Column[] = [{ key: "parameter", label: "Название параметра" }];

  // Добавляем колонки для всех 12 месяцев
  for (let i = 1; i <= 12; i++) {
    cols.push({ key: `month_${i}` as MonthColumn, label: monthNames[i - 1] });
  }

  return cols;
});

// Трансформированные данные: строки = параметры, колонки = месяцы
const tableData = computed(() => {
  if (!currentMonthReport.value) return [];

  // Собираем все вложенные параметры в один Set для быстрой проверки
  const nestedParamsSet = new Set<string>();
  Object.values(NESTED_PARAMS).forEach((nestedKeys) => {
    nestedKeys.forEach((key) => nestedParamsSet.add(key));
  });

  // Фильтруем параметры, исключая вложенные
  const filteredParams = parameters.filter(param => !nestedParamsSet.has(param.key));

  return filteredParams.map(param => {
    const row: TableRow = {
      parameter: param.label,
      paramKey: param.key
    };

    // Для виртуальных параметров не заполняем данные (только заголовок)
    if (isVirtualParam(param.key)) {
      for (let i = 1; i <= 12; i++) {
        row[`month_${i}`] = "-";
      }
    } else {
      // Для реальных параметров извлекаем данные из MonthlyData
      const monthlyData = (currentMonthReport.value as Report)[param.key as MonthlyDataKeys];

      if (monthlyData) {
        for (let i = 1; i <= 12; i++) {
          const monthKey = getMonthKey(i);
          row[`month_${i}`] = monthlyData[monthKey];
        }
      } else {
        // Если данных нет, заполняем прочерками
        for (let i = 1; i <= 12; i++) {
          row[`month_${i}`] = "-";
        }
      }
    }

    return row;
  });
});

// Реф на контейнер таблицы для горизонтального скролла
const scrollContainer = ref<HTMLElement | null>(null);
// Реф на body таблицы для вертикального скролла
const tableBody = ref<HTMLElement | null>(null);

// Флаг. Наличие скролла у контейнера таблицы
const hasScroll = ref(false);

// Ref для отслеживания раскрытых параметров
const expandedParams = ref<Set<string>>(new Set());

// Объект скроллбара
let ps: PerfectScrollbar | null = null;
let resizeObserverInstance: ResizeObserver | null = null;

// Проверка, раскрыт ли параметр
const isParamExpanded = (paramKey: string) => expandedParams.value.has(paramKey);

// Функция раскрытия/закрытия вложенных параметров
const toggleParamExpansion = (paramKey: string) => {
  const newSet = new Set(expandedParams.value);
  if (newSet.has(paramKey)) {
    newSet.delete(paramKey);
  } else {
    newSet.add(paramKey);
  }
  expandedParams.value = newSet;
  updateScroll();
};

// Проверка, есть ли вложенные параметры
const hasNestedParams = (paramKey: string): boolean => {
  return paramKey in NESTED_PARAMS;
};

// Получение вложенных параметров для родительского параметра
const getNestedParams = (parentKey: string) => {
  const nestedKeys = NESTED_PARAMS[parentKey] || [];
  return nestedKeys
    .map((key) => {
      // Ищем в allParameters, чтобы найти вложенные параметры
      const param = allParameters.find(p => p.key === key);
      if (param) {
        return { key, label: param.label };
      }
      return null;
    })
    .filter((item): item is { key: string; label: string } => item !== null);
};

// Обновление скролла
const updateScroll = () => {
  setTimeout(() => {
    checkScroll();
    ps?.update();
  }, 0);
};

// Получение значения вложенного параметра для конкретного месяца
const getNestedParamValue = (paramKey: string, monthNumber: number) => {
  if (!currentMonthReport.value) return "-";

  // Для виртуальных параметров возвращаем прочерк
  if (isVirtualParam(paramKey)) {
    return "-";
  }

  // Получаем MonthlyData объект по ключу параметра
  const monthlyData = (currentMonthReport.value as Report)[paramKey as MonthlyDataKeys];

  if (!monthlyData) return "-";

  // Преобразуем номер месяца в ключ MonthlyData
  const monthKey = getMonthKey(monthNumber);
  const value = monthlyData[monthKey];
  
  if (value !== null) {
    return value.toLocaleString("ru-RU");
  } else {
    return "-";
  }
};

// Форматирование значения ячейки в зависимости от типа
const formatCellValue = (row: TableRow, columnKey:  keyof TableRow | `month_${number}`) => {
  const value = row[columnKey];

  // Для колонки "parameter" возвращаем название как есть
  if (columnKey === "parameter") {
    return value ?? "-";
  }

  if (value !== null) {
    return value.toLocaleString("ru-RU");
  } else {
    return "-";
  }
};

// Проверка наличия скролла у контейнера таблицы.
const checkScroll = () => {
  if (tableBody.value) {
    hasScroll.value = tableBody.value.scrollHeight > tableBody.value.clientHeight;
  }
};

// Автоматически раскрываем все параметры с вложенными элементами при первой загрузке
watch(tableData, (newData) => {
  if (newData.length > 0 && expandedParams.value.size === 0) {
    const paramsToExpand = new Set<string>();

    // Раскрываем параметры первого уровня
    newData.forEach(row => {
      if (hasNestedParams(row.paramKey)) {
        paramsToExpand.add(row.paramKey);

        // Раскрываем параметры второго уровня
        const nestedParams = getNestedParams(row.paramKey);
        nestedParams.forEach(nestedParam => {
          if (hasNestedParams(nestedParam.key)) {
            paramsToExpand.add(nestedParam.key);
          }
        });
      }
    });

    expandedParams.value = paramsToExpand;
  }
}, { immediate: true });

onMounted(() => {
  // Инициализация скролла на tableBody (оба направления)
  if (tableBody.value) {
    ps = new PerfectScrollbar(tableBody.value, {
      wheelSpeed: 1,
      wheelPropagation: false,
      minScrollbarLength: 20
    });

    checkScroll();

    // Синхронизация горизонтального скролла с header (кроме зафиксированных колонок)
    tableBody.value.addEventListener("ps-scroll-x", () => {
      const tableBodyEl = tableBody.value;
      const scrollContainerEl = scrollContainer.value;

      if (scrollContainerEl && tableBodyEl) {
        const header = scrollContainerEl.querySelector(".main-table__header") as HTMLElement;
        if (header) {
          // Применяем transform только к незафиксированным колонкам
          const headerCols = header.querySelectorAll(".main-table__col--header:not(.main-table__col--sticky)");
          headerCols.forEach((col) => {
            (col as HTMLElement).style.transform = `translateX(-${tableBodyEl.scrollLeft}px)`;
          });
        }
      }
    });

    // Обсервер на изменения размера контейнера для обновления позиции скролла
    resizeObserverInstance = new ResizeObserver(() => {
      checkScroll();
      ps?.update();
    });
    resizeObserverInstance.observe(tableBody.value);
  }
});

onBeforeUnmount(() => {
  if (resizeObserverInstance) {
    resizeObserverInstance.disconnect();
    resizeObserverInstance = null;
  }
  if (ps) {
    ps.destroy();
    ps = null;
  }
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", {
        ref_key: "scrollContainer",
        ref: scrollContainer,
        class: "main-table__scroll-container"
      }, [
        _createElementVNode("div", {
          class: _normalizeClass([{ 'main-table__header--with-scroll': hasScroll.value }, "main-table__header main-table__row main-table__row--header"]),
          style: { gridTemplateColumns: 'minmax(700px, 2fr) repeat(12, minmax(120px, 1fr))' }
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(columns.value, (column, idx) => {
            return (_openBlock(), _createElementBlock("div", {
              key: column.key,
              class: _normalizeClass(["main-table__col main-table__col--header", { 'main-table__col--sticky': idx === 0 }])
            }, _toDisplayString(column.label), 3))
          }), 128))
        ], 2),
        _createElementVNode("div", {
          ref_key: "tableBody",
          ref: tableBody,
          class: "main-table__body"
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tableData.value, (row, index) => {
            return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
              _createElementVNode("div", _hoisted_3, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(columns.value, (column, idx) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: column.key,
                    class: _normalizeClass(["main-table__col", {
                  'main-table__col--sticky': idx === 0,
                  'main-table__col--expandable': idx === 0 && hasNestedParams(row.paramKey)
                }])
                  }, [
                    (idx === 0 && hasNestedParams(row.paramKey))
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 0,
                          class: _normalizeClass(["expand-button expand-button--nested", { 'expand-button--expanded': isParamExpanded(row.paramKey) }]),
                          onClick: ($event: any) => (toggleParamExpansion(row.paramKey))
                        }, [
                          _createVNode(IconChevronUp)
                        ], 10, _hoisted_4))
                      : _createCommentVNode("", true),
                    _createElementVNode("div", _hoisted_5, _toDisplayString(formatCellValue(row, column.key)), 1)
                  ], 2))
                }), 128))
              ]),
              (hasNestedParams(row.paramKey) && isParamExpanded(row.paramKey))
                ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(getNestedParams(row.paramKey), (nestedParam) => {
                    return (_openBlock(), _createElementBlock(_Fragment, {
                      key: nestedParam.key
                    }, [
                      _createElementVNode("div", {
                        class: _normalizeClass(["main-table__row main-table__row--nested", { 'main-table__row--with-scroll': hasScroll.value }]),
                        style: { gridTemplateColumns: '32px minmax(668px, 2fr) repeat(12, minmax(120px, 1fr))' }
                      }, [
                        _cache[0] || (_cache[0] = _createElementVNode("div", { class: "main-table__col main-table__col--spacer main-table__col--sticky" }, null, -1)),
                        _createElementVNode("div", _hoisted_6, [
                          (hasNestedParams(nestedParam.key))
                            ? (_openBlock(), _createElementBlock("button", {
                                key: 0,
                                class: _normalizeClass(["expand-button expand-button--nested", { 'expand-button--expanded': isParamExpanded(nestedParam.key) }]),
                                onClick: ($event: any) => (toggleParamExpansion(nestedParam.key))
                              }, [
                                _createVNode(IconChevronUp)
                              ], 10, _hoisted_7))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", _hoisted_8, _toDisplayString(nestedParam.label), 1)
                        ]),
                        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(12, (i) => {
                          return _createElementVNode("div", {
                            key: `month_${i}`,
                            class: "main-table__col"
                          }, [
                            _createElementVNode("div", _hoisted_9, _toDisplayString(getNestedParamValue(nestedParam.key, i)), 1)
                          ])
                        }), 64))
                      ], 2),
                      (hasNestedParams(nestedParam.key) && isParamExpanded(nestedParam.key))
                        ? (_openBlock(true), _createElementBlock(_Fragment, { key: 0 }, _renderList(getNestedParams(nestedParam.key), (nestedParam2) => {
                            return (_openBlock(), _createElementBlock("div", {
                              key: nestedParam2.key,
                              class: _normalizeClass(["main-table__row main-table__row--nested main-table__row--nested-2", { 'main-table__row--with-scroll': hasScroll.value }]),
                              style: { gridTemplateColumns: '64px minmax(636px, 2fr) repeat(12, minmax(120px, 1fr))' }
                            }, [
                              _cache[1] || (_cache[1] = _createElementVNode("div", { class: "main-table__col main-table__col--spacer main-table__col--sticky" }, null, -1)),
                              _createElementVNode("div", _hoisted_10, [
                                _createElementVNode("div", _hoisted_11, _toDisplayString(nestedParam2.label), 1)
                              ]),
                              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(12, (i) => {
                                return _createElementVNode("div", {
                                  key: `month_${i}`,
                                  class: "main-table__col"
                                }, [
                                  _createElementVNode("div", _hoisted_12, _toDisplayString(getNestedParamValue(nestedParam2.key, i)), 1)
                                ])
                              }), 64))
                            ], 2))
                          }), 128))
                        : _createCommentVNode("", true)
                    ], 64))
                  }), 128))
                : _createCommentVNode("", true)
            ], 64))
          }), 128))
        ], 512)
      ], 512)
    ])
  ]))
}
}

})